import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { DataContext } from "../context/contextApi";
import { toAppVideo, parseYoutubeId, deriveTitleFromUrl } from "../utils/videoHelper";
import SearchResultVideoCard from "../components/SearchResultVideoCard";
import UseOnline from "../utils/UseOnline";
import OfflineComponent from "../shared/OfflineComponent";
import ShimmerSearchResultVideoCard from "../shared/ShimmerSearchResultVideoCard";

const SearchResult = () => {
  const [result, setResult] = useState("");
  const { searchQuery } = useParams();
  const decodedQuery = searchQuery ? decodeURIComponent(searchQuery) : "";
  const { setLoading, firebaseVideos, firebaseLoading, addToast } = useContext(DataContext);
  const [deepLoading, setDeepLoading] = useState(false);
  const [deepResults, setDeepResults] = useState(null);
  const [deepError, setDeepError] = useState(null);
  const [deepRawResponse, setDeepRawResponse] = useState(null);

  const isOnline = UseOnline();
  // Client will use relative /api/... routes so CRA dev server can proxy to local server

  useEffect(() => {
    document.getElementById("root").classList.remove("custom-h");

    const fetchSearchResults = async () => {
      setLoading(true);
      try {
        // Wait for Firebase data to load before searching
        if (firebaseLoading) {
          setResult([]);
          return;
        }

        // Search through Firebase videos instead of API
        if (!firebaseVideos || firebaseVideos.length === 0) {
          setResult([]);
          return;
        }

        const query = decodedQuery.toLowerCase();
        const filtered = firebaseVideos.filter((video) => {
          const title = (video?.title || "").toLowerCase();
          const author = (video?.author?.title || "").toLowerCase();
          const tag = (video?.customTag || "").toLowerCase();
          return title.includes(query) || author.includes(query) || tag.includes(query);
        });

        // Convert to the expected format with { type: "video", video: {...} }
        const formattedResults = filtered.map((video) => ({
          type: "video",
          video: video,
        }));

        setResult(formattedResults);
      } catch (e) {
        console.error("fetchSearchResults error:", e);
        setResult([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [decodedQuery, firebaseVideos, firebaseLoading, setLoading]);

  // Reset deep-search UI state when the search query changes
  useEffect(() => {
    setDeepLoading(false);
    setDeepResults(null);
    setDeepError(null);
    setDeepRawResponse(null);
  }, [decodedQuery]);

  // Compute whether deep-search was blocked by server response (memoized)
  const deepBlocked = useMemo(() => {
    if (!deepRawResponse) return false;
    try {
      if (typeof deepRawResponse === 'object' && deepRawResponse.message) {
        return String(deepRawResponse.message).toLowerCase().includes('content not allowed');
      }
      if (typeof deepRawResponse === 'string') return deepRawResponse.toLowerCase().includes('content not allowed');
    } catch (e) {}
    return false;
  }, [deepRawResponse]);

  // Extracted Deep Search logic as a stable callback so it can be auto-triggered
  const doDeepSearch = useCallback(async () => {
    if (!decodedQuery) return;
    setDeepLoading(true);
    setDeepResults(null);
    try {
      addToast('Running deep search — fetching external sources...', 'info', 4000);
      // First try the raw endpoint so we can see what scrapers return
      const rawUrl = `/api/deep-search-raw?q=${encodeURIComponent(decodedQuery)}`;
      console.log('Calling deep-search-raw URL:', rawUrl);
      setDeepError(null);
      setDeepRawResponse(null);
      const rawResp = await fetch(rawUrl);
      let items = [];
      if (rawResp.ok) {
        const contentType = rawResp.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const rawData = await rawResp.json();
          console.log('deep-search-raw JSON response', rawData);
          setDeepRawResponse(rawData);
          items = Array.isArray(rawData && rawData.videos ? rawData.videos : rawData) ? (rawData.videos || rawData) : [];
        } else {
          const rawText = await rawResp.text();
          setDeepRawResponse(rawText);
          const tryParse = (text) => {
            try { return JSON.parse(text); } catch (e) {}
            const first = text.indexOf('[');
            const last = text.lastIndexOf(']');
            if (first !== -1 && last !== -1 && last > first) {
              const sub = text.slice(first, last + 1);
              try { return JSON.parse(sub); } catch (e) {}
            }
            const fObj = text.indexOf('{');
            const lObj = text.lastIndexOf('}');
            if (fObj !== -1 && lObj !== -1 && lObj > fObj) {
              const sub = text.slice(fObj, lObj + 1);
              try { return JSON.parse(sub); } catch (e) {}
            }
            return null;
          };
          const parsed = tryParse(rawText);
          if (parsed) {
            console.log('deep-search-raw parsed JSON from text', parsed);
            setDeepRawResponse(parsed);
            items = Array.isArray(parsed && parsed.videos ? parsed.videos : parsed) ? (parsed.videos || parsed) : [];
          } else {
            setDeepError(`raw endpoint returned non-JSON response (content-type: ${contentType})`);
          }
        }

        if (items.length > 0) {
          // normalization logic (same as before) - reuse existing block to setDeepResults
          const extractUrl = (v) => {
            if (!v) return null;
            return (
              v.url || v.link || v.src || v.pluginUrl || v.video?.url || v.video_url || v.video?.link || v.oembed?.url || v.href || null
            );
          };

          const extractThumbnails = (v) => {
            if (!v) return null;
            if (v.thumbnails && Array.isArray(v.thumbnails) && v.thumbnails.length) {
              // Filter out invalid thumbnails
              const validThumbs = v.thumbnails.filter(t => t && t.url && t.url.startsWith('http'));
              if (validThumbs.length > 0) return validThumbs;
            }
            if (v.thumbnail && typeof v.thumbnail === 'string' && v.thumbnail.startsWith('http')) {
              return [{ url: v.thumbnail }];
            }
            if (v.thumbnail_url && typeof v.thumbnail_url === 'string' && v.thumbnail_url.startsWith('http')) {
              return [{ url: v.thumbnail_url }];
            }
            if (v.oembed && v.oembed.thumbnail_url && v.oembed.thumbnail_url.startsWith('http')) {
              return [{ url: v.oembed.thumbnail_url }];
            }
            if (v.image && typeof v.image === 'string' && v.image.startsWith('http')) {
              return [{ url: v.image }];
            }
            if (v.cover && typeof v.cover === 'string' && v.cover.startsWith('http')) {
              return [{ url: v.cover }];
            }
            return null;
          };

          const normalizeDeepItems = (itemsArray, limit = 50) => {
            if (!Array.isArray(itemsArray)) return [];
            const decodeSafe = (s) => {
              if (!s || typeof s !== 'string') return s;
              try {
                if (!s.startsWith('http') && s.indexOf('%3A') !== -1) {
                  const d = decodeURIComponent(s);
                  if (d && d.length) return d;
                }
                if (/%[0-9A-Fa-f]{2}/.test(s)) {
                  try { return decodeURIComponent(s); } catch (e) { /* ignore */ }
                }
              } catch (e) { /* ignore */ }
              return s;
            };

            const mapped = itemsArray.map((v) => {
              const url = extractUrl(v) || '';
              const isFb = (v.platform === 'facebook' || v.source === 'facebook') || (url && (url.includes('facebook.com') || url.includes('fb.watch')));
              if (isFb) {
                const embedUrl = v.embed || v.pluginUrl || `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&t=0`;
                const thumbs = extractThumbnails(v);
                const titleRaw = v.title || v.name || v.videoTitle || '';
                const title = decodeSafe(titleRaw);
                const fbVideo = {
                  videoId: v.videoId || (url ? encodeURIComponent(url) : null),
                  originalUrl: v.originalUrl || url,
                  url: url,
                  platform: 'facebook',
                  source: 'facebook',
                  title: title || 'Facebook Video',
                  thumbnails: thumbs,
                  thumbnail: v.thumbnail || (thumbs && thumbs[0]?.url),
                  stats: v.stats || { views: v.views || 0 },
                  publishedTimeText: v.publishedTimeText || v.published || '',
                  lengthSeconds: v.lengthSeconds || v.duration || null,
                  embed: embedUrl,
                  pluginUrl: embedUrl,
                  src: embedUrl,
                };
                return fbVideo;
              }

              const thumbs = extractThumbnails(v);
              const titleRaw = v.title || v.name || v.videoTitle || v.caption || v.text || '';
              const title = decodeSafe(titleRaw);
              const itemForApp = {
                url,
                originalUrl: v.originalUrl || url,
                title,
                thumbnails: thumbs,
                thumbnail: v.thumbnail || (thumbs && thumbs[0]?.url),
                videoId: v.videoId || v.id || parseYoutubeId(url) || undefined,
                description: v.description || v.descriptionSnippet || v.snippet || v.summary || v.caption || '',
                stats: v.stats || v.views ? { views: v.views || (v.stats && v.stats.views) } : undefined,
                platform: v.platform || v.source,
                source: v.source || v.platform,
              };

              const appVideo = toAppVideo(itemForApp);
              if (appVideo) return appVideo;

              return {
                videoId: itemForApp.videoId || (url ? encodeURIComponent(url) : null),
                originalUrl: url || null,
                url: url || null,
                platform: parseYoutubeId(url) ? 'youtube' : null,
                source: parseYoutubeId(url) ? 'youtube' : null,
                title: itemForApp.title,
                thumbnails: itemForApp.thumbnails || (thumbs ? thumbs : [{ url: '/default-thumbnail.svg' }]),
                thumbnail: itemForApp.thumbnail,
                stats: itemForApp.stats || { views: 0 },
                publishedTimeText: v.publishedTimeText || v.published || v.ago || '',
                lengthSeconds: v.lengthSeconds || v.seconds || null,
              };
            })
            .filter((x) => x && ( (x.thumbnails && Array.isArray(x.thumbnails) && x.thumbnails.length > 0) || x.url || x.originalUrl));

            mapped.sort((a, b) => {
              const order = (p) => (p === 'facebook' ? 0 : (p === 'youtube' ? 1 : 2));
              return order(a.platform) - order(b.platform);
            });

            return mapped.slice(0, limit);
          };

          let normalized = normalizeDeepItems(items, 50);
          normalized = normalized.map((v) => {
            const url = (v.url || v.originalUrl || '').toString().toLowerCase();
            const platform = v.platform || (url.includes('facebook.com') || url.includes('fb.watch') ? 'facebook' : v.platform);
            const thumbs = (v.thumbnails && v.thumbnails.length) ? v.thumbnails : (v.thumbnail ? [{ url: v.thumbnail }] : null);
            let embed = v.embed;
            if (platform === 'facebook' && !embed) {
              embed = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(v.originalUrl || v.url)}&show_text=false&t=0`;
            }
            return Object.assign({}, v, { platform, source: platform, thumbnails: thumbs, embed, pluginUrl: embed, src: embed });
          });

          normalized = normalized.filter((v) => {
            const thumb = v.thumbnails && v.thumbnails[0]?.url;
            // Filter out videos without valid thumbnails
            if (!thumb) return false;
            if (thumb === '/default-thumbnail.svg') return false;
            if (thumb.length < 10) return false;
            // Ensure thumbnail is a valid HTTP/HTTPS URL
            if (!thumb.startsWith('http://') && !thumb.startsWith('https://')) return false;
            return true;
          });
          console.log('deep-search-raw normalized items (after thumbnail filter)', normalized);
          
          // Show appropriate toast based on results
          if (normalized.length === 0) {
            addToast('Deep Search completed — no videos found', 'warning', 4000);
          } else {
            addToast(`Deep Search found ${normalized.length} video${normalized.length > 1 ? 's' : ''}`, 'success', 3000);
          }
          
          setDeepResults(normalized);
          setDeepLoading(false);
          return;
        }
      } else {
        console.warn('deep-search-raw failed', rawResp.statusText);
        setDeepError(`raw endpoint failed: ${rawResp.status} ${rawResp.statusText}`);
      }

      // Fallback: call full flow which includes AI processing
      const resp = await fetch(`/api/deep-search?q=${encodeURIComponent(decodedQuery)}`);
      if (resp.ok) {
        let respText = await resp.text();
        let data;
        try {
          data = JSON.parse(respText);
        } catch (err) {
          data = respText;
          setDeepError('server returned non-JSON response from AI endpoint');
        }
        console.log('deep-search response', data);
        setDeepRawResponse(data);
        const items = Array.isArray(data && data.videos ? data.videos : data) ? (data.videos || data) : [];
        const normalizeDeepItems = (itemsArray, limit = 50) => {
          if (!Array.isArray(itemsArray)) return [];

          const decodeSafe = (s) => {
            if (!s || typeof s !== 'string') return s;
            try {
              if (!s.startsWith('http') && s.indexOf('%3A') !== -1) {
                const d = decodeURIComponent(s);
                if (d && d.length) return d;
              }
              if (/%[0-9A-Fa-f]{2}/.test(s)) {
                try { return decodeURIComponent(s); } catch (e) { /* ignore */ }
              }
            } catch (e) { /* ignore */ }
            return s;
          };

          const mapped = itemsArray.map((v) => {
            const url = v.url || v.link || v.originalUrl || '';
            const urlLower = (url || '').toLowerCase();
            const isFb = urlLower.includes('facebook.com') || urlLower.includes('fb.watch') || v.platform === 'facebook' || v.source === 'facebook';

            const rawTitle = v.title || v.name || '';
            let title = decodeSafe(rawTitle);

            // If title still looks like an encoded URL or is a URL, derive a friendlier title from the URL
            const looksLikeEncodedUrl = typeof title === 'string' && (title.toLowerCase().includes('%3a') || title.startsWith('https%3A') || title.startsWith('http%3A'));
            const looksLikeUrl = typeof title === 'string' && (/^https?:\/\//i.test(title) || /^https%3A/i.test(title));
            if ((!title || looksLikeEncodedUrl || looksLikeUrl) && url) {
              const fromUrl = deriveTitleFromUrl(url) || '';
              if (fromUrl && fromUrl.length > 0 && fromUrl !== url) title = fromUrl;
            }

            // Build embed URL for Facebook
            const embedUrl = isFb ? (v.embed || v.pluginUrl || `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&t=0`) : null;

            return {
              title: title || '',
              descriptionSnippet: v.descriptionSnippet || v.description || '',
              stats: { views: (v.stats && v.stats.views) || v.views || 0 },
              publishedTimeText: v.publishedTimeText || v.published || '',
              lengthSeconds: v.lengthSeconds || v.seconds || null,
              videoId: v.videoId || (isFb ? encodeURIComponent(url) : (v.url && v.url.match(/v=([\w-]{11})/) ? (v.url.match(/v=([\w-]{11})/)[1]) : undefined)),
              thumbnails: (() => {
                // Validate and extract thumbnails with proper URL checking
                if (v.thumbnail && typeof v.thumbnail === 'string' && v.thumbnail.startsWith('http')) {
                  return [{ url: v.thumbnail }];
                }
                if (v.thumbnails && Array.isArray(v.thumbnails) && v.thumbnails.length > 0) {
                  const validThumbs = v.thumbnails.filter(t => t && t.url && t.url.startsWith('http'));
                  if (validThumbs.length > 0) return validThumbs;
                }
                if (v.thumbnail_url && typeof v.thumbnail_url === 'string' && v.thumbnail_url.startsWith('http')) {
                  return [{ url: v.thumbnail_url }];
                }
                return undefined;
              })(),
              thumbnail: v.thumbnail || (v.thumbnails && v.thumbnails[0]?.url),
              url: url,
              originalUrl: v.originalUrl || url,
              platform: isFb ? 'facebook' : (v.videoId ? 'youtube' : null),
              source: isFb ? 'facebook' : (v.videoId ? 'youtube' : null),
              embed: embedUrl,
              pluginUrl: embedUrl,
              src: embedUrl,
            };
          })
          .filter((x) => {
            // More strict filtering - require valid thumbnails
            if (!x) return false;
            const hasThumbs = x.thumbnails && Array.isArray(x.thumbnails) && x.thumbnails.length > 0;
            const hasValidThumb = hasThumbs && x.thumbnails[0]?.url && x.thumbnails[0].url.startsWith('http');
            return hasValidThumb || x.url;
          });

          mapped.sort((a, b) => {
            const order = (p) => (p === 'facebook' ? 0 : (p === 'youtube' ? 1 : 2));
            return order(a.platform) - order(b.platform);
          });

          return mapped.slice(0, limit);
        };

        let normalized = normalizeDeepItems(items, 50);
        normalized = normalized.map((v) => {
          const url = (v.url || v.originalUrl || '').toString().toLowerCase();
          const platform = v.platform || (url.includes('facebook.com') || url.includes('fb.watch') ? 'facebook' : v.platform);
          const thumbs = (v.thumbnails && v.thumbnails.length) ? v.thumbnails : (v.thumbnail ? [{ url: v.thumbnail }] : null);
          let embed = v.embed;
          if (platform === 'facebook' && !embed) {
            embed = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(v.originalUrl || v.url)}&show_text=false&t=0`;
          }
          return Object.assign({}, v, { platform, source: platform, thumbnails: thumbs, embed, pluginUrl: embed, src: embed });
        });

        normalized = normalized.filter((v) => {
          const thumb = v.thumbnails && v.thumbnails[0]?.url;
          // Filter out videos without valid thumbnails
          if (!thumb) return false;
          if (thumb === '/default-thumbnail.svg') return false;
          if (thumb.length < 10) return false;
          // Ensure thumbnail is a valid HTTP/HTTPS URL
          if (!thumb.startsWith('http://') && !thumb.startsWith('https://')) return false;
          return true;
        });
        console.log('deep-search normalized items (after thumbnail filter)', normalized);
        
        // Show appropriate toast based on results
        if (normalized.length === 0) {
          addToast('Deep Search completed — no videos found', 'warning', 4000);
        } else {
          addToast(`Deep Search found ${normalized.length} video${normalized.length > 1 ? 's' : ''}`, 'success', 3000);
        }
        
        setDeepResults(normalized);
      } else {
        console.warn('deep-search failed', resp.statusText);
        setDeepError(`AI endpoint failed: ${resp.status} ${resp.statusText}`);
      }
    } catch (err) {
      console.error('doDeepSearch error:', err);
      setDeepError(err.message || 'Unknown error');
      addToast('Deep Search failed — please try again', 'error', 3000);
    } finally {
      setDeepLoading(false);
    }
  }, [decodedQuery, addToast]);

  // Auto-trigger deep search when no local results and deep search wasn't run yet
  useEffect(() => {
    if (!isOnline) return;
    if (deepBlocked) return;
    if (Array.isArray(result) && result.length === 0 && deepResults === null && !deepLoading) {
      const id = setTimeout(() => {
        addToast('No local results — running Deep Search automatically', 'info', 3500);
        doDeepSearch();
      }, 600);
      return () => clearTimeout(id);
    }
  }, [result, deepResults, deepLoading, deepBlocked, isOnline, addToast, doDeepSearch]);

  // deepBlocked is memoized earlier using useMemo
  // (kept for compatibility)

  return (
    <div className="flex flex-1">

      <Helmet>
        <title>{`Search: ${decodedQuery} - Anime Forever Tamil`}</title>
        <meta name="description" content={`Search results for ${decodedQuery} across multiple platforms.`} />
        <link rel="canonical" href={window.location.origin + window.location.pathname} />
      </Helmet>

      {!isOnline && (
        <div className="grow w-full h-full overflow-y-auto bg-black">
          <OfflineComponent />
        </div>
      )}
      {isOnline && (
        <div className="grow w-full min-h-screen overflow-y-auto bg-white dark:bg-black">
          <div className="w-full bg-transparent grid grid-cols-1 gap-2 p-5">
            {/* Show shimmer while Firebase is loading or search results are being fetched */}
            {(result === "" || firebaseLoading) ? (
              Array(20)
                .fill("")
                .map((e, index) => {
                  return <ShimmerSearchResultVideoCard key={index} />;
                })
            ) : (
              (Array.isArray(result) && result.length === 0) ? (
                <div className={`w-full ${deepResults ? 'py-4' : 'py-20'} flex items-center justify-center`}>
                  <div className="text-center">
                    {deepResults === null && (
                      deepBlocked ? (
                        <>
                          <p className="text-lg font-semibold text-black dark:text-white">Content not allowed</p>
                          <p className="mt-2 text-sm text-black/[0.7] dark:text-white/[0.7]">Search only anime or cartoon related terms. Try another anime or cartoon title.</p>
                        </>
                      ) : (
                        <>
                          <p className="text-lg font-semibold text-black dark:text-white">No results found</p>
                          <p className="mt-2 text-sm text-black/[0.7] dark:text-white/[0.7]">Try a different search term or check spelling.</p>
                        </>
                      )
                    )}
                    {deepResults === null && (
                      <div className="mt-4">
                        {/* Only show Deep Search button when not blocked */}
                        {!deepBlocked && (
                        <button
                          className={`inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-semibold rounded-full shadow-md transform transition hover:scale-105 focus:outline-none`}
                          onClick={async () => doDeepSearch()}
                        >
                        <span className="flex items-center gap-2">
                          <span className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full text-sm">🤖</span>
                          <span className="whitespace-nowrap">{deepLoading ? 'Searching...' : 'Deep Search'}</span>
                        </span>
                        <span className="ml-3 inline-flex items-center justify-center text-xs font-medium bg-white/20 px-2 py-0.5 rounded-full">BETA</span>
                      </button>
                      )}
                    </div>
                    )}
                    {/* Show deep results below when available */}
                    {deepResults && (
                      <div className="mt-6">
                        {deepResults.length === 0 ? (
                          <div>
                            <p className="text-sm text-black/[0.7] dark:text-white/[0.7]">
                              Deep Search couldn't find any videos for "{decodedQuery}". Try different keywords.
                            </p>
                            {deepError && <p className="text-xs text-red-500 mt-2">Error: {deepError}</p>}
                          </div>
                        ) : (
                          deepResults.map((video, idx) => (
                            <SearchResultVideoCard key={`deep-${idx}`} video={video} />
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                result?.map((item, index) => {
                  if (item?.type !== "video") return false;
                  return (
                    <SearchResultVideoCard key={index} video={item?.video} />
                  );
                })
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResult;